import * as React from "react";
import { Omit } from "react-redux";

import { emptyRef } from "../../../TypingUtils";
import { Sticky } from "../../Sticky/Sticky";

import { Footer, FooterProps } from "./Footer/Footer";
import { Root } from "./MultiRow.styles";
import { Sidebar, SidebarProps } from "./Sidebar";

interface ExpectProps {
    setRefSticky: (x: null | Sticky) => void;
    setStop: (x: Element) => void;
}

interface State {
    height: number;
}

type Props = Omit<FooterProps & SidebarProps, keyof ExpectProps>;

export class MultiRow extends React.Component<Props> {
    public stop: HTMLElement | null = emptyRef;
    public sticky: null | Sticky = emptyRef;
    private readonly multiRow = React.createRef<HTMLTableRowElement>();
    private observer: ResizeObserver | null = null;
    public state: State = { height: 0 };

    public componentDidMount(): void {
        this.sticky?.setStop(this.stop ?? undefined);
        this.observer = new ResizeObserver(() => {
            this.setState({ height: this.getMultiRowHeight() });
        });

        if (this.multiRow.current) {
            this.observer.observe(this.multiRow.current);
        }
        this.setState({ height: this.getMultiRowHeight() });
    }

    public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
        return (
            nextState.height !== this.state.height ||
            nextProps.horizontalScroll !== this.props.horizontalScroll ||
            nextProps.isLastRow !== this.props.isLastRow ||
            nextProps.children !== this.props.children
        );
    }

    public componentWillUnmount(): void {
        this.setState({ height: 0 });
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    private getMultiRowHeight(): number {
        return this.multiRow.current?.getBoundingClientRect().height ?? 0;
    }

    public render(): JSX.Element {
        return (
            <tr data-type="multiline-row" ref={this.multiRow}>
                <Root colSpan={this.props.columnWidth.length}>
                    <Sidebar
                        maxOccurs={this.props.maxOccurs}
                        bindingPath={this.props.bindingPath}
                        buttonType={this.props.buttonType}
                        columnWidth={this.props.columnWidth}
                        doNotCopyPaths={this.props.doNotCopyPaths}
                        isList={!this.props.isPager}
                        setStop={this.refStop}
                        optional={this.props.optional}
                        isReadOnly={this.props.isReadOnly}
                        filteredUniqKey={this.props.filteredUniqKey}>
                        {this.props.children}
                    </Sidebar>
                    {(this.props.isAddButton || this.props.isPager) && (
                        <Footer
                            maxOccurs={this.props.maxOccurs}
                            bindingPath={this.props.bindingPath}
                            horizontalScroll={this.props.horizontalScroll}
                            isLastRow={this.props.isLastRow}
                            setRefSticky={this.refSticky}
                            buttonText={this.props.buttonText}
                            width={this.props.width}
                            isPager={this.props.isPager}
                            isAddButton={this.props.isAddButton}
                            filteredUniqKey={this.props.filteredUniqKey}
                        />
                    )}
                </Root>
            </tr>
        );
    }

    private readonly refStop = (el: null | HTMLElement) => (this.stop = el);
    private readonly refSticky = (el: null | Sticky) => (this.sticky = el);
}
